import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, LogIn } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import ImageWithSEO from '@/components/ImageWithSEO';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { t, language, toggleLanguage } = useLanguage();
  const { branding } = useSiteSettings();

  const allNavItems = [
    { path: '/', label: t('home') },
    { path: '/menu', label: t('menu') },
    { path: '/about', label: t('about') },
    { path: '/reservations', label: t('reservations') },
    { path: '/catering', label: t('catering') },
    { path: '/contact', label: t('contact') },
    { path: '/rewards', label: 'Rewards' },
    { path: '/blog', label: t('blog') },
  ];

  const navItems = allNavItems;

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            {branding.logo_url ? (
              <ImageWithSEO
                src={branding.logo_url}
                alt="Capital District Social - American bar and restaurant logo"
                context="Navigation Logo"
                keywords={['Capital District Social', 'Tallahassee restaurant', 'American bar']}
                className="h-12 md:h-14 w-auto"
                width={120}
                height={48}
                includeStructuredData={false}
              />
            ) : (
              <div className="flex flex-col">
                <span className="text-lg md:text-xl font-display font-bold text-primary tracking-tight">Capital District</span>
                <span className="text-xs md:text-sm font-medium text-muted-foreground tracking-widest uppercase">Social</span>
              </div>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-md transition-all duration-200 font-medium text-sm tracking-wide ${
                  isActive(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Button
              variant="ghost"
              onClick={toggleLanguage}
              className="ml-4 h-10 px-3 rounded-md hover:bg-muted"
              title={`Switch to ${language === 'en' ? 'Spanish' : 'English'}`}
            >
              <Globe className="h-4 w-4 mr-2" />
              <span className="text-xs font-medium">{language === 'en' ? 'ES' : 'EN'}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              asChild
              className="ml-2 border-primary/50 hover:bg-primary hover:text-primary-foreground"
            >
              <Link to="/management">
                <LogIn className="h-4 w-4 mr-2" />
                {t('login')}
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              aria-label={`Switch to ${language === 'en' ? 'Spanish' : 'English'}`}
            >
              <Globe className="h-5 w-5" />
            </Button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-md hover:bg-muted transition-colors"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden pb-4 animate-fade-in">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-md transition-colors font-medium text-sm ${
                  isActive(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/management"
              onClick={() => setIsOpen(false)}
              className="block px-4 py-3 mt-2"
            >
              <Button variant="outline" size="sm" className="w-full border-primary/50">
                <LogIn className="h-4 w-4 mr-2" />
                {t('login')}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
